<template>
  <div
    class="invitees-summary"
    :class="{ 'invitees-summary--loading': createStore.isLoading }"
    data-test-id="participants-summary"
  >
    <div class="d-flex justify-space-between align-center mb-3">
      <span class="text-grey-darken-4 font-weight-bold">
        {{ i18n.t('titles.who_needs_to_sign') }}
      </span>
      <v-btn
        class="text-none"
        color="primary"
        data-test-id="edit-participants"
        density="comfortable"
        variant="text"
        @click="workflow.openFromCard(2)"
      >
        {{ i18n.t('buttons.edit') }}
      </v-btn>
    </div>

    <div
      v-for="(group, index) in groups"
      :key="index"
      class="d-flex align-center ga-4"
      :class="{ 'invitees-summary__group--boxed': isSequence }"
      :data-test-id="`participant-group-${index}`"
    >
      <div v-if="isSequence" class="invitees-summary__badge">
        {{ index + 1 }}
      </div>

      <div class="invitees-summary__cards">
        <QuorumApproverGroupCard
          v-if="isApproverQuorumGroup(group)"
          :group="group"
        />
        <QuorumGroupCard
          v-else-if="isQuorumGroup(group)"
          :attachments-allowed="attachmentsAllowed"
          :first-signee-id="firstSigneeId"
          :group="group"
        />
        <template v-else>
          <InviteeCard
            v-for="participant in group"
            :key="participant.signeeId"
            :attachments-allowed="showsAttachment(participant)"
            :participant="participant"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCreateStore } from '@/stores/create';
import { useSigningWorkflow } from '@/composables/create/useSigningWorkflow';
import InviteeCard from '@/components/create/InviteeCard.vue';
import QuorumGroupCard from '@/components/create/QuorumGroupCard.vue';
import QuorumApproverGroupCard from '@/components/create/QuorumApproverGroupCard.vue';
import { SigneeType } from '@/types/enums/SigneeType';
import type { Signee } from '@/types/Signee';

const i18n = useI18n();
const createStore = useCreateStore();
const workflow = useSigningWorkflow();

// Committed sign-order groups (never the dialog's in-progress draft). Empty
// groups are dropped so the numbering matches what the user actually configured.
// Each group is normalized so approvers always render first — the backend does
// not preserve the in-group position, so a reloaded document could otherwise
// place an approver between two signees.
const groups = computed(() =>
  createStore.committedSigneesOrdered
    .filter((group) => group.length > 0)
    .map((group) => orderApproversFirst(group))
);

// Returns a copy of the group with approvers moved to the front, keeping the
// existing order for signees of the same type (stable sort). Never mutates the
// store's arrays.
function orderApproversFirst(group: Signee[]): Signee[] {
  return [...group].sort((a, b) => {
    if (a.signeeType === b.signeeType) return 0;
    return a.signeeType === SigneeType.APPROVE ? -1 : 1;
  });
}
// Group boxes and numbered badges only apply to a real sequence — 2+ groups. A
// single group renders without a box or badge, but still shows its quorum card
// when it has one: an ungrouped list can carry a quorum too (see
// useQuorumEligibility).
const isSequence = computed(() => groups.value.length > 1);

// A group renders as the shared-slot quorum card instead of individual
// InviteeCards only when it actually has a quorum (requiredSignatures /
// requiredApprovals > 0) AND has 2+ members — a single-member group (or one
// with no quorum set) keeps the normal per-signee cards. Applies to both
// SIGN and APPROVE groups, rendered via QuorumGroupCard / QuorumApproverGroupCard
// respectively (see isApproverQuorumGroup below).
function isQuorumGroup(group: Signee[]): boolean {
  if (group.length <= 1) return false;
  const quorum = createStore.getGroupQuorum(group);
  return quorum != null && quorum > 0;
}

// Whether a quorum group's members are approvers rather than signees — used
// to pick QuorumApproverGroupCard (no shared signature slots) over
// QuorumGroupCard.
function isApproverQuorumGroup(group: Signee[]): boolean {
  return isQuorumGroup(group) && group[0]?.signeeType === SigneeType.APPROVE;
}

// Only the first signee in order may attach documents, so the paperclip
// affordance is limited to them.
const attachmentsAllowed = computed(
  () => createStore.document?.signeeAttachmentsAllowed ?? false
);
const firstSigneeId = computed(() => {
  for (const group of groups.value) {
    const firstSignee = group.find(
      (participant) => participant.signeeType === SigneeType.SIGN
    );
    if (firstSignee) return firstSignee.signeeId;
  }
  return null;
});

function showsAttachment(participant: Signee) {
  return (
    attachmentsAllowed.value && participant.signeeId === firstSigneeId.value
  );
}
</script>

<style scoped lang="scss">
.invitees-summary {
  transition: opacity 0.2s ease;
}

// While signatures are being placed/removed (createStore.isLoading), dim the
// list and block interaction so the user can't keep placing mid-operation.
.invitees-summary--loading {
  opacity: 0.5;
  pointer-events: none;
}

.invitees-summary__group--boxed {
  background: rgb(var(--v-theme-grey-lighten-4));
  border-radius: 12px;
  padding: 12px;

  &:not(:last-child) {
    margin-bottom: 12px;
  }
}

.invitees-summary__badge {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgb(var(--v-theme-primary), 0.2);
  color: rgb(var(--v-theme-primary));
  font-size: 16px;
}

.invitees-summary__cards {
  flex: 1 1 auto;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}
</style>
